import React, { useEffect, useRef, useState, useContext } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { COLORS } from '../../../../utils/Application_Constants';
import { SplitExpenseTabDeleteBlockedStatuses } from '../ExpenseStatus.enum';

const useStyles = makeStyles((theme) => ({
    tabLabel: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    textFieldRoot: {
        marginBottom: 0,
        '& .MuiOutlinedInput-input': {
            padding: '4px !important',
            outline: 'none !important',
            border: 'none !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            outline: 'none !important',
            border: 'none !important',
        },
    },
    tabLabelInput: {
        cursor: 'pointer',
        fontSize: 14,
    },
}));

const ExpenseTabLabel = (props) => {
    const {
        handleRemoveTab,
        index,
        isActive,
        setValue,
        tab,
        mode,
        isViewOnly,
        originalPrimaryExpense,
        isAnyExpensePosted,
    } = props;
    const classes = useStyles();
    const [isEditing, setIsEditing] = useState(false);
    const ref = useRef();
    const { errors, watch } = useFormContext();
    const expenseEntries = watch('expenseEntries');
    const [hideDelete, setHideDelete] = useState(false);
    const flags = useFlags();
    const { ltoeWebExpensesEnableExpenseTabLabelEdit: enableEditButtonByFF } = flags;
    const handleBlur = () => {
        setIsEditing(false);
    };

    const handleEdit = () => {
        ref.current.focus();
        ref.current.select();
        setIsEditing((prevState) => !prevState);
    };

    useEffect(() => {
        if (mode === 'pending-cc' && expenseEntries[index].id > 0) {
            setHideDelete(true);
        }
    }, []);

    const hasErrors =
        !!Object.keys(errors?.expenseEntries?.[index] || {}).length ||
        !!Object.keys(errors?.fileUploads?.[index] || {}).length;

    // A split expense group (primary + splits) is approved/posted/in-correction/invoiced/
    // invoice-review as a single unit, but each tab carries its own status and the `tab`
    // prop is a non-reactive snapshot that can lag the live form, so we derive the group's
    // lifecycle from whether ANY entry (reactively watched) is in a blocked status.
    const isGroupInDeleteBlockedStatus = (expenseEntries || []).some((entry) =>
        SplitExpenseTabDeleteBlockedStatuses.includes(entry?.status)
    );
    // Once the group is in a blocked status, saved splits (id > 0) can no longer be removed.
    // A newly added, not-yet-saved split (id is null) can still be deleted so the user can
    // back out of adding it, even while the parent sits in one of those statuses.
    // Same carve-out for the posted check below: `isAnyExpensePosted` is derived from the
    // originally-loaded entries, so it stays true while a posted expense is being corrected.
    // Saved splits are still protected by `isSplitDeleteBlockedByStatus` (Posted and
    // InCorrection are both blocked statuses), so gating on `isCurrentSplitSaved` only
    // restores the ability to back out of a split added during the correction.
    const isCurrentSplitSaved = expenseEntries?.[index]?.id > 0;
    const isSplitDeleteBlockedByStatus = isGroupInDeleteBlockedStatus && isCurrentSplitSaved;

    return (
        <div className={classes.tabLabel}>
            <Controller
                defaultValue={mode === 'vault' ? 'Vault Charge' : tab.label}
                name={`expenseEntries[${index}].label`}
                rules={{
                    required: 'Tabs must be named',
                }}
                render={({ onChange, value }) => (
                    <TextField
                        onClick={() => setValue(index)}
                        onChange={onChange}
                        onBlur={handleBlur}
                        classes={{ root: classes.textFieldRoot }}
                        value={value}
                        InputProps={{
                            readOnly: !isEditing,
                        }}
                        inputProps={{ className: classes.tabLabelInput }}
                        inputRef={ref}
                        required
                        error={hasErrors}
                        helperText={errors.expenseEntries?.[index]?.label?.message}
                        sx={{
                            '& .MuiOutlinedInput-input': {
                                color: !hasErrors ? 'default' : `${COLORS.LT_PEPPER} !important`,
                            },
                        }}
                    />
                )}
            />
            {enableEditButtonByFF && isActive && !isViewOnly && (
                <EditIcon fontSize="small" color="primary" onClick={handleEdit} />
            )}
            {index !== 0 &&
                !isSplitDeleteBlockedByStatus &&
                !hideDelete &&
                !isViewOnly &&
                !(mode !== 'pending-cc' && isAnyExpensePosted && isCurrentSplitSaved) && (
                    <CloseIcon fontSize="small" onClick={() => handleRemoveTab(index)} />
                )}
        </div>
    );
};

export default ExpenseTabLabel;
